import type {RequestWithBodyInit} from "./types.ts";

/**
 * Copy a Request object with a new URL.
 *
 * It is difficult to make copies of Request objects with modified URLs. This
 * function does as good as is currently possible to copy a request with a new
 * URL, while maintaining all of the properties of the old Request, including
 * the body. It returns a Promise which resolves to the new Request.
 *
 * This solution comes from http://stackoverflow.com/a/34641566/57318
 * According to https://github.com/whatwg/fetch/issues/191, this should
 * eventually be equivalent to `new Request(url, request)`, but that doesn't
 * yet work.
 *
 * @param url The new URL to use for the request.
 * @param request The Request object to copy.
 * @returns A Promise that resolves to the new Request object.
 */
export function copyRequestWithUrl(
    url: string,
    request: RequestWithBodyInit,
): Promise<Request> {
    // If we are using the `fetch` polyfill, then we can't be guaranteed that
    // `Blob` will work correctly, so we just reach into the request and pull
    // out the body.
    const bodyPromise: Promise<
        string | null | undefined | Blob | null | undefined
    > =
        typeof jest !== "undefined"
            ? Promise.resolve(request._bodyInit)
            : request.headers.get("Content-Type")
              ? request.blob()
              : Promise.resolve(undefined);

    return bodyPromise.then((body) => {
        return new Request(url, {
            body: body,
            method: request.method,
            headers: request.headers,
            referrer: request.referrer,
            referrerPolicy: request.referrerPolicy,
            mode: request.mode,
            credentials: request.credentials,
            cache: request.cache,
            redirect: request.redirect,
            integrity: request.integrity,
            signal: request.signal,
        });
    });
}
